import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast"; // For feedback
import { Phone, Mail, Info, Send } from 'lucide-react';
import { Helmet } from 'react-helmet-async'; // Import Helmet
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from "@/config/emailjs-config";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const ContactPage = () => {
  const form = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Prepare template parameters for EmailJS
    const templateParams = {
      to_email: EMAILJS_CONFIG.RECIPIENT_EMAIL,
      from_name: formData.name,
      from_email: formData.email,
      subject: formData.subject,
      message: formData.message,
    };
    
    try {
      // Using EmailJS to send the form data directly to your email
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );
      
      console.log('Email sent successfully:', result.text);
      
      setIsSubmitting(false);
      toast({
        title: "Bericht Verzonden!",
        description: "Bedankt voor uw bericht. We nemen zo snel mogelijk contact op.",
        variant: "default",
      });
      
      // Clear form
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Failed to send email:', error);
      setIsSubmitting(false);
      toast({
        title: "Fout bij verzenden",
        description: "Er is iets misgegaan bij het verzenden van uw bericht. Probeer het later opnieuw.",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-16 md:space-y-24"
    >
      <Helmet>
        <title>Contacteer CylostreamWeb | Webdesign & AI in Limburg</title>
        <meta name="description" content="Neem contact op met CylostreamWeb voor webdesign, website ontwikkeling of AI automatisatie in Limburg, België. Vraag een offerte aan of stel uw vraag." />
        <meta name="keywords" content="contact webdesign Limburg, offerte website België, AI specialist contact, CylostreamWeb contact, webbureau Hasselt" />
        <meta property="og:title" content="Contacteer CylostreamWeb | Webdesign & AI in Limburg" />
        <meta property="og:description" content="Neem contact op met CylostreamWeb voor webdesign, website ontwikkeling of AI automatisatie in Limburg, België. Vraag een offerte aan of stel uw vraag." />
        <link rel="canonical" href="https://www.cylostream.com/contact" /> 
      </Helmet>
      
      {/* Intro Section */}
      <motion.section variants={fadeInUp} className="text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Neem <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Contact</span> Op
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
          Heeft u een vraag of wilt u een project bespreken? We horen graag van u! Vul het formulier in of gebruik onze contactgegevens.
        </p>
      </motion.section>

      {/* Contact Info & Form Section */}
      <motion.section variants={staggerContainer} className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16 items-start">
        {/* Contact Details */}
        <motion.div variants={fadeInUp} className="lg:col-span-1 space-y-6 bg-slate-50 p-8 rounded-lg border">
          <h2 className="text-2xl font-semibold mb-4">Contactgegevens</h2>
          <div className="flex items-start space-x-3">
            <Mail size={20} className="mt-1 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-medium">E-mail</h3>
              <a href="mailto:contact@cylostream.com" className="text-muted-foreground hover:text-primary transition-colors">contact@cylostream.com</a>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Phone size={20} className="mt-1 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-medium">Telefoon</h3>
              <a href="tel:+32468110615" className="text-muted-foreground hover:text-primary transition-colors">+32 468 11 06 15</a>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Info size={20} className="mt-1 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-medium">BTW Nummer</h3>
              <p className="text-muted-foreground">BE1022.402.368</p>
            </div>
          </div>
          {/* Add Address here if needed later */}
        </motion.div>

        {/* Contact Form */}
        <motion.form 
          variants={fadeInUp} 
          onSubmit={handleSubmit}
          ref={form}
          className="lg:col-span-2 space-y-6 bg-card p-8 rounded-lg border"
        >
          <h2 className="text-2xl font-semibold mb-4">Stuur Ons Een Bericht</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Naam</Label>
              <Input id="name" name="name" placeholder="Uw volledige naam" required value={formData.name} onChange={handleChange} disabled={isSubmitting} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input id="email" name="email" type="email" placeholder="voorbeeld@domein.be" required value={formData.email} onChange={handleChange} disabled={isSubmitting} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">Onderwerp</Label>
            <Input id="subject" name="subject" placeholder="Waarover gaat uw bericht?" required value={formData.subject} onChange={handleChange} disabled={isSubmitting} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Bericht</Label>
            <Textarea id="message" name="message" placeholder="Typ hier uw vraag of opmerking..." required rows={5} value={formData.message} onChange={handleChange} disabled={isSubmitting} />
          </div>
          <Button type="submit" size="lg" className="w-full md:w-auto" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verzenden...
              </>
            ) : (
              <>
                <Send size={18} className="mr-2"/> Bericht Verzenden
              </>
            )}
          </Button>
        </motion.form>
      </motion.section>

    </motion.div>
  );
};

export default ContactPage; 